import { createPortal } from 'react-dom';
// src/components/TaskKanban/TaskKanbanModal.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import {
  X, Plus, Save, Edit2, Trash2, Flag, Calendar, Users,
  Building2, Briefcase, UserCheck, Settings, Eye, Search, GripVertical, TrendingUp
} from 'lucide-react';
import api from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { useConfirm } from '../../contexts/ConfirmContext';

// ================= Типы =================
interface TaskStage {
  _id: string;
  title: string;
  color: string;
  order: number;
}

interface TaskItem {
  _id: string;
  title: string;
  description: string;
  creatorId: string;
  creatorName: string;
  observers: string[];
  stageId: string;
  priority: 'low' | 'medium' | 'high';
  dueDate: string;
  attachments: any[];
  linkedTo: { type: string | null; id: string | null };
}

interface User {
  id: string;
  name: string;
  email: string;
}

interface TaskKanbanModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TaskKanbanModal: React.FC<TaskKanbanModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const confirm = useConfirm();

  const [stages, setStages] = useState<TaskStage[]>([]);
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [viewMode, setViewMode] = useState<'my' | 'observing'>('my');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [observedUserName, setObservedUserName] = useState<string>('');

  const [showStageModal, setShowStageModal] = useState(false);
  const [editingStage, setEditingStage] = useState<TaskStage | null>(null);
  const [stageForm, setStageForm] = useState({ title: '', color: '#3498db' });

  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskItem | null>(null);
  const [taskForm, setTaskForm] = useState({
    title: '', description: '', priority: 'medium' as string, dueDate: '',
    linkedTo: { type: null as string | null, id: null as string | null },
    attachments: [] as any[],
    stageId: '' as string,
  });
  const [taskFormError, setTaskFormError] = useState('');

  const [clients, setClients] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [candidates, setCandidates] = useState<any[]>([]);
  const [vacancies, setVacancies] = useState<any[]>([]);
  const [selectedLinkedType, setSelectedLinkedType] = useState('');
  const [selectedLinkedId, setSelectedLinkedId] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [stagesRes, usersRes] = await Promise.all([
        api.get('/task-kanban/stages'),
        api.get('/users/company-users')
      ]);
      
      const stagesData = Array.isArray(stagesRes.data) ? stagesRes.data : [];
      setStages(stagesData.sort((a: TaskStage, b: TaskStage) => a.order - b.order));
      setUsers(usersRes.data);

      let tasksRes;
      if (viewMode === 'observing' && selectedUserId) {
        tasksRes = await api.get(`/task-kanban/tasks?creatorId=${selectedUserId}`);
        const selectedUser = usersRes.data.find((u: User) => u.id === selectedUserId);
        setObservedUserName(selectedUser?.name || 'Пользователь');
      } else {
        tasksRes = await api.get('/task-kanban/tasks');
        setObservedUserName('');
      }
      setTasks(tasksRes.data);

      const [clientsRes, employeesRes, candidatesRes, vacanciesRes] = await Promise.all([
        api.get('/clients'),
        api.get('/employees'),
        api.get('/candidates'),
        api.get('/vacancies')
      ]);
      setClients(clientsRes.data);
      setEmployees(employeesRes.data);
      setCandidates(candidatesRes.data);
      setVacancies(vacanciesRes.data);
    } catch (err) {
      console.error('Ошибка загрузки данных канбана:', err);
    } finally {
      setLoading(false);
    }
  }, [viewMode, selectedUserId]);

  useEffect(() => { if (isOpen) fetchData(); }, [isOpen, fetchData]);
  useEffect(() => { if (isOpen) fetchData(); }, [selectedUserId, viewMode, isOpen, fetchData]);

  const filteredTasks = tasks.filter(t =>
    t.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.creatorName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTasksByStage = (stageId: string) =>
    filteredTasks.filter(t => t.stageId === stageId);

  // --- Drag & Drop Handler (с поддержкой перемещения колонок) ---
  const onDragEnd = async (result: DropResult) => {
    const { source, destination, draggableId, type } = result;
    
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    // Перемещение колонок
    if (type === 'COLUMN') {
      const newStages = Array.from(stages);
      const [movedStage] = newStages.splice(source.index, 1);
      newStages.splice(destination.index, 0, movedStage);
      
      const reorderedStages = newStages.map((stage, idx) => ({
        ...stage,
        order: idx
      }));
      
      setStages(reorderedStages);
      
      try {
        const payload = {
          stages: reorderedStages.map(s => ({ id: s._id }))
        };
        await api.put('/task-kanban/stages/reorder', payload);
      } catch (err) {
        console.error('Ошибка сохранения порядка колонок:', err);
        setStages(stages);
      }
      return;
    }

    // Перемещение задачи
    const task = tasks.find(t => t._id === draggableId);
    if (!task || task.stageId === destination.droppableId) return;
    if (task.creatorId !== user?.id || viewMode !== 'my') {
      return;
    }
    
    const oldTasks = [...tasks];
    setTasks(prev => prev.map(t => t._id === draggableId ? { ...t, stageId: destination.droppableId } : t));
    
    try {
      await api.put(`/task-kanban/tasks/${draggableId}`, { stageId: destination.droppableId });
    } catch (err) {
      console.error('Ошибка перемещения задачи:', err);
      setTasks(oldTasks);
    }
  };

  // Функция для смены этапа задачи через селект
  const changeTaskStage = async (taskId: string, newStageId: string) => {
    const task = tasks.find(t => t._id === taskId);
    if (!task || task.stageId === newStageId) return;

    const oldTasks = [...tasks];
    setTasks(prev => prev.map(t => t._id === taskId ? { ...t, stageId: newStageId } : t));

    try {
      await api.put(`/task-kanban/tasks/${taskId}`, { stageId: newStageId });
    } catch (err) {
      console.error('Ошибка смены этапа:', err);
      setTasks(oldTasks);
    }
  };

  // Вспомогательные функции для цвета
  const hexToRgb = (hex: string) => {
    const h = hex.replace('#', '');
    return {
      r: parseInt(h.substring(0, 2), 16),
      g: parseInt(h.substring(2, 4), 16),
      b: parseInt(h.substring(4, 6), 16),
      rgb: `${parseInt(h.substring(0, 2), 16)}, ${parseInt(h.substring(2, 4), 16)}, ${parseInt(h.substring(4, 6), 16)}`
    };
  };

  const getStageColor = (stageId: string) => {
    const stage = stages.find(s => s._id === stageId);
    return stage?.color || '#888888';
  };

  const openStageModal = (stage?: TaskStage) => {
    setEditingStage(stage || null);
    setStageForm({ title: stage?.title || '', color: stage?.color || '#3498db' });
    setShowStageModal(true);
  };

  const saveStage = async () => {
    if (!stageForm.title) return;
    try {
      if (editingStage?._id) {
        const res = await api.put(`/task-kanban/stages/${editingStage._id}`, stageForm);
        setStages(prev => prev.map(s => s._id === editingStage._id ? res.data : s));
      } else {
        const res = await api.post('/task-kanban/stages', stageForm);
        setStages(prev => [...prev, res.data]);
      }
      setShowStageModal(false);
    } catch (err: any) { 
      console.error('Ошибка сохранения этапа:', err);
    }
  };

  const deleteStage = async (id: string, title: string) => {
    confirm.showConfirm({
      title: 'Удаление этапа',
      message: `Удалить этап "${title}"? Все задачи на этом этапе будут удалены.`,
      onConfirm: async () => {
        await api.delete(`/task-kanban/stages/${id}`);
        setStages(prev => prev.filter(s => s._id !== id));
        setTasks(prev => prev.filter(t => t.stageId !== id));
      }
    });
  };

  const openTaskModal = (task?: TaskItem) => {
    setTaskFormError('');
    if (task) {
      setEditingTask(task);
      setTaskForm({
        title: task.title, description: task.description,
        priority: task.priority, dueDate: task.dueDate?.split('T')[0] || '',
        linkedTo: task.linkedTo, attachments: task.attachments,
        stageId: task.stageId,
      });
      setSelectedLinkedType(task.linkedTo?.type || '');
      setSelectedLinkedId(task.linkedTo?.id || '');
    } else {
      setEditingTask(null);
      setTaskForm({
        title: '', description: '', priority: 'medium', dueDate: '',
        linkedTo: { type: null, id: null }, attachments: [],
        stageId: stages.length > 0 ? stages[0]._id : '',
      });
      setSelectedLinkedType(''); setSelectedLinkedId('');
    }
    setShowTaskModal(true);
  };

  const saveTask = async () => {
    if (!taskForm.title || !taskForm.dueDate) { 
      setTaskFormError('Заполните название и дату'); 
      return; 
    }
    
    const stageId = editingTask ? taskForm.stageId : (taskForm.stageId || stages[0]?._id || '');
    
    if (!stageId) { 
      setTaskFormError('Выберите этап'); 
      return; 
    }
    
    const payload = {
      ...taskForm,
      linkedTo: selectedLinkedType && selectedLinkedId ? { type: selectedLinkedType, id: selectedLinkedId } : { type: null, id: null },
      stageId: stageId,
    };
    
    try {
      if (editingTask) {
        const res = await api.put(`/task-kanban/tasks/${editingTask._id}`, payload);
        setTasks(prev => prev.map(t => t._id === editingTask._id ? res.data : t));
      } else {
        const res = await api.post('/task-kanban/tasks', payload);
        setTasks(prev => [...prev, res.data]);
      }
      setShowTaskModal(false);
    } catch (err: any) { 
      setTaskFormError(err.response?.data?.error || 'Ошибка сохранения'); 
    }
  };

  const deleteTask = async (id: string) => {
    confirm.showConfirm({
      title: 'Удаление задачи',
      message: 'Удалить задачу?',
      onConfirm: async () => {
        await api.delete(`/task-kanban/tasks/${id}`);
        setTasks(prev => prev.filter(t => t._id !== id));
      }
    });
  };

  const getLinkedIcon = (type: string) => {
    switch (type) {
      case 'client': return <Building2 size={12} />;
      case 'employee': return <Users size={12} />;
      case 'candidate': return <UserCheck size={12} />;
      case 'vacancy': return <Briefcase size={12} />;
      default: return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles.root}>
      {/* Глобальные стили dnd + красивые скроллбары */}
      <style>{`
        body > div[data-rfd-draggable-context-id] {
          z-index: 9999999 !important;
          position: fixed !important;
          top: 0 !important; left: 0 !important;
          width: 100% !important; height: 100% !important;
          pointer-events: none !important;
        }
        body > div[data-rfd-draggable-context-id] > div {
          z-index: 9999999 !important;
          pointer-events: auto !important;
        }
        [data-rfd-drag-handle-context-id] { z-index: 9999999 !important; }
        #task-kanban-board::-webkit-scrollbar { height: 6px; }
        #task-kanban-board::-webkit-scrollbar-track { background: transparent; }
        #task-kanban-board::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.2); border-radius: 3px; }
        #task-kanban-board::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,0.35); }

        .task-card {
          transition: all 0.25s cubic-bezier(0.2, 0.0, 0, 1.0);
        }
        .task-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 16px rgba(0,0,0,0.12);
        }

        .column-container {
          transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
        }

        .glass-effect {
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
        }

        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 rgba(37,117,252,0.4); }
          70% { box-shadow: 0 0 0 8px rgba(37,117,252,0); }
          100% { box-shadow: 0 0 0 0 rgba(37,117,252,0); }
        }
        .dragging-pulse {
          animation: pulse 1.5s infinite;
        }
      `}</style>

      {/* ========== ШАПКА ========== */}
      <div style={styles.header} className="glass-effect">
        <h2 style={styles.title}>📋 Канбан задач</h2>
        <button onClick={onClose} style={styles.closeBtn}><X size={20}/></button>
      </div>

      {/* ========== ПАНЕЛЬ УПРАВЛЕНИЯ ========== */}
      <div style={styles.toolbar} className="glass-effect">
        <button
          onClick={() => { setViewMode('my'); setSelectedUserId(''); }}
          style={{
            ...styles.modeBtn,
            background: viewMode === 'my' ? 'linear-gradient(135deg, #6a11cb, #2575fc)' : 'transparent',
            color: viewMode === 'my' ? 'white' : '#2575fc',
            borderColor: viewMode === 'my' ? 'transparent' : '#2575fc',
          }}
        >
          <Eye size={15}/> Мои задачи
        </button>
        <div style={styles.observerBox} className="glass-effect">
          <Users size={15} style={{ color: '#666', marginRight: 6 }}/>
          <select
            value={selectedUserId}
            onChange={e => {
              const id = e.target.value;
              setSelectedUserId(id);
              if (id) setViewMode('observing'); else setViewMode('my');
            }}
            style={styles.select}
          >
            <option value="">Наблюдать за...</option>
            {users.filter(u => u.id !== user?.id).map(u => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        </div>
        <div style={styles.searchBox} className="glass-effect">
          <Search size={14} style={{ color: '#999', marginRight: 6 }}/>
          <input
            placeholder="Поиск задач..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>
        <button onClick={() => openTaskModal()} style={styles.btnPrimary}>
          <Plus size={15}/> Создать задачу
        </button>
        <button onClick={() => openStageModal()} style={styles.btnSecondary}>
          <Settings size={15}/> Настроить этапы
        </button>
      </div>

      {viewMode === 'observing' && selectedUserId && (
        <div style={styles.hint}>👁 Вы просматриваете задачи пользователя <strong>{observedUserName}</strong>. Редактирование запрещено.</div>
      )}

      {/* ========== КАНБАН-ДОСКА С ПЕРЕТАСКИВАЕМЫМИ КОЛОНКАМИ ========== */}
      {loading ? (
        <div style={styles.loading}>Загрузка...</div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="board" type="COLUMN" direction="horizontal">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                id="task-kanban-board"
                style={styles.board}
              >
                {stages.map((stage, index) => {
                  const stageColor = stage.color;
                  const rgb = hexToRgb(stageColor).rgb;
                  const stageTasks = getTasksByStage(stage._id);
                  
                  return (
                    <Draggable key={stage._id} draggableId={`column-${stage._id}`} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className="column-container"
                          style={{
                            ...styles.column,
                            background: snapshot.isDragging
                              ? `rgba(${rgb}, 0.18)`
                              : `rgba(${rgb}, 0.06)`,
                            borderTop: `3px solid ${stageColor}`,
                            borderColor: snapshot.isDragging ? '#00E5FF' : 'rgba(255,255,255,0.3)',
                            boxShadow: snapshot.isDragging
                              ? `0 0 24px rgba(${rgb}, 0.35), inset 0 0 20px rgba(${rgb}, 0.1)`
                              : '0 4px 20px rgba(0,0,0,0.06)',
                            opacity: snapshot.isDragging ? 0.85 : 1,
                            ...provided.draggableProps.style
                          }}
                        >
                          {/* Заголовок колонки с ручкой для перетаскивания */}
                          <div style={styles.colHead}>
                            <div 
                              {...provided.dragHandleProps} 
                              style={{ 
                                cursor: 'grab', 
                                display: 'flex', 
                                alignItems: 'center', 
                                marginRight: 4, 
                                opacity: 0.5,
                                padding: '4px 0'
                              }}
                            >
                              <GripVertical size={16} color="#666" />
                            </div>
                            <span style={{ ...styles.colDot, background: stageColor, boxShadow: `0 0 8px ${stageColor}80` }}/>
                            <span style={styles.colTitle}>{stage.title}</span>
                            <span style={{ ...styles.colCount, background: `linear-gradient(135deg, ${stageColor}, ${stageColor}cc)` }}>
                              {stageTasks.length}
                            </span>
                            {viewMode === 'my' && (
                              <div style={styles.colActions}>
                                <button onClick={(e) => { e.stopPropagation(); openStageModal(stage); }} style={styles.colActBtn}><Edit2 size={13}/></button>
                                <button onClick={(e) => { e.stopPropagation(); deleteStage(stage._id, stage.title); }} style={styles.colActBtn}><Trash2 size={13} color="#ff4d4f"/></button>
                              </div>
                            )}
                          </div>

                          {/* Зона для задач */}
                          <Droppable droppableId={stage._id} type="TASK">
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                style={{
                                  ...styles.colBody,
                                  background: snapshot.isDraggingOver ? `rgba(${rgb},0.15)` : 'transparent',
                                  transition: 'background 0.2s ease',
                                  minHeight: 60,
                                  borderRadius: 12,
                                }}
                              >
                                {stageTasks.map((task, idx) => {
                                  const taskStageColor = getStageColor(task.stageId);
                                  const taskRgb = hexToRgb(taskStageColor).rgb;
                                  return (
                                    <Draggable
                                      key={task._id}
                                      draggableId={task._id}
                                      index={idx}
                                      isDragDisabled={viewMode !== 'my' || task.creatorId !== user?.id}
                                    >
                                      {(provided, snapshot) => {
                                        const activeColor = snapshot.isDragging ? stageColor : taskStageColor;
                                        const activeRgb = hexToRgb(activeColor).rgb;
                                        return (
                                          <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            className="task-card"
                                            style={{
                                              ...styles.card,
                                              background: snapshot.isDragging
                                                ? `linear-gradient(135deg, rgba(${activeRgb}, 0.9), rgba(${activeRgb}, 0.7))`
                                                : `linear-gradient(135deg, rgba(${taskRgb}, 0.25), rgba(${taskRgb}, 0.12))`,
                                              borderLeft: `4px solid ${activeColor}`,
                                              boxShadow: snapshot.isDragging
                                                ? `0 25px 40px rgba(0,0,0,0.35), 0 0 0 2px ${activeColor}, inset 0 0 15px rgba(255,255,255,0.2)`
                                                : '0 2px 6px rgba(0,0,0,0.04)',
                                              transform: snapshot.isDragging ? 'rotate(2deg) scale(1.04)' : 'none',
                                              backdropFilter: snapshot.isDragging ? 'blur(4px)' : 'none',
                                              border: snapshot.isDragging ? `1px solid ${activeColor}40` : '1px solid rgba(0,0,0,0.04)',
                                              transition: snapshot.isDragging ? 'none' : 'all 0.2s ease',
                                              cursor: snapshot.isDragging ? 'grabbing' : 'pointer',
                                              ...provided.draggableProps.style,
                                            }}
                                            onClick={() => openTaskModal(task)}
                                          >
                                            <div style={styles.cardTop}>
                                              <span style={{...styles.cardTitle, color: snapshot.isDragging ? '#fff' : '#1a1a2e'}}>
                                                {task.title}
                                              </span>
                                              <div style={{display: 'flex', gap: 4}}>
                                                {task.priority === 'high' && <Flag size={14} color="#ff4d4f"/>}
                                                {viewMode === 'my' && task.creatorId === user?.id && (
                                                  <button
                                                    onClick={(e) => { e.stopPropagation(); deleteTask(task._id); }}
                                                    style={{background:'transparent', border:'none', cursor:'pointer', padding:0}}
                                                  >
                                                    <Trash2 size={13} color="#ff4d4f"/>
                                                  </button>
                                                )}
                                              </div>
                                            </div>
                                            
                                            {/* Быстрая смена этапа внутри карточки */}
                                            {viewMode === 'my' && task.creatorId === user?.id && (
                                              <div style={{ marginBottom: 6 }} onClick={(e) => e.stopPropagation()}>
                                                <select
                                                  value={task.stageId}
                                                  onChange={(e) => changeTaskStage(task._id, e.target.value)}
                                                  style={{
                                                    width: '100%',
                                                    padding: '3px 8px',
                                                    borderRadius: 6,
                                                    border: `1px solid ${activeColor}40`,
                                                    background: 'rgba(255,255,255,0.7)',
                                                    fontSize: 11,
                                                    outline: 'none',
                                                    cursor: 'pointer',
                                                    color: '#333',
                                                  }}
                                                >
                                                  {stages.map(s => (
                                                    <option key={s._id} value={s._id}>{s.title}</option>
                                                  ))}
                                                </select>
                                              </div>
                                            )}
                                            
                                            <div style={{...styles.cardMeta, color: snapshot.isDragging ? 'rgba(255,255,255,0.85)' : '#555'}}>
                                              <Calendar size={11}/> {new Date(task.dueDate).toLocaleDateString()}
                                              <Users size={11}/> {task.creatorName}
                                            </div>
                                            {task.linkedTo?.type && (
                                              <div style={{...styles.cardLink, color: snapshot.isDragging ? 'rgba(255,255,255,0.9)' : '#2575fc'}}>
                                                {getLinkedIcon(task.linkedTo.type)}
                                                <span>
                                                  {task.linkedTo.type === 'client' ? 'Клиент' :
                                                   task.linkedTo.type === 'employee' ? 'Сотрудник' :
                                                   task.linkedTo.type === 'candidate' ? 'Кандидат' : 'Вакансия'}
                                                </span>
                                              </div>
                                            )}
                                            {task.attachments?.length > 0 && (
                                              <div style={{...styles.cardFiles, color: snapshot.isDragging ? 'rgba(255,255,255,0.7)' : '#888'}}>
                                                📎 {task.attachments.length}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      }}
                                    </Draggable>
                                  );
                                })}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {/* ========== МОДАЛКА ЭТАПА ========== */}
      {showStageModal && (
        <div style={modalOverlay} onClick={() => setShowStageModal(false)}>
          <div style={modalBox} onClick={e => e.stopPropagation()}>
            <div style={modalHead}>
              <h3>{editingStage ? 'Редактировать' : 'Новый этап'}</h3>
              <button onClick={() => setShowStageModal(false)} style={modalClose}><X size={18}/></button>
            </div>
            <input
              placeholder="Название этапа"
              value={stageForm.title}
              onChange={e => setStageForm(p => ({ ...p, title: e.target.value }))}
              style={modalInput}
              autoFocus
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
              <span style={{ fontSize: 13, color: '#555' }}>Цвет:</span>
              <input
                type="color"
                value={stageForm.color}
                onChange={e => setStageForm(p => ({ ...p, color: e.target.value }))}
                style={{ width: 36, height: 32, border: 'none', borderRadius: 6, cursor: 'pointer' }}
              />
            </div>
            <div style={modalFooter}>
              <button onClick={saveStage} style={modalBtnPrimary}><Save size={14}/> Сохранить</button>
            </div>
          </div>
        </div>
      )}

      {/* ========== МОДАЛКА ЗАДАЧИ (С ВЫБОРОМ ЭТАПА) ========== */}
      {showTaskModal && createPortal(
        <div style={modalOverlay} onClick={() => setShowTaskModal(false)}>
          <div style={{ ...modalBox, width: 560 }} onClick={e => e.stopPropagation()}>
            <div style={modalHead}>
              <h3>{editingTask ? 'Редактировать задачу' : 'Новая задача'}</h3>
              <button onClick={() => setShowTaskModal(false)} style={modalClose}><X size={18}/></button>
            </div>
            {taskFormError && <div style={errBlock}>{taskFormError}</div>}
            
            <input 
              placeholder="Название *" 
              value={taskForm.title} 
              onChange={e => setTaskForm(p => ({...p, title: e.target.value}))} 
              style={modalInput}
              autoFocus
            />
            
            <textarea 
              placeholder="Описание" 
              value={taskForm.description} 
              onChange={e => setTaskForm(p => ({...p, description: e.target.value}))} 
              style={{...modalInput, minHeight: 80, marginTop: 10, resize: 'vertical'}}
            />
            
            {/* Выбор этапа */}
            <div style={{ marginTop: 10 }}>
              <label style={{ fontSize: 12, color: '#888', marginBottom: 4, display: 'block', fontWeight: 600 }}>
                <TrendingUp size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                Этап *
              </label>
              <select
                value={taskForm.stageId}
                onChange={e => setTaskForm(p => ({...p, stageId: e.target.value}))}
                style={{
                  ...modalInput,
                  cursor: 'pointer',
                  border: '2px solid rgba(37,117,252,0.3)',
                  background: 'rgba(37,117,252,0.03)',
                  fontWeight: 500,
                }}
              >
                <option value="">Выберите этап</option>
                {stages.map(s => (
                  <option key={s._id} value={s._id}>{s.title}</option>
                ))}
              </select>
            </div>
            
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginTop:10}}>
              <div>
                <label style={{ fontSize: 11, color: '#888', marginBottom: 2, display: 'block' }}>Приоритет</label>
                <select 
                  value={taskForm.priority} 
                  onChange={e => setTaskForm(p => ({...p, priority: e.target.value}))} 
                  style={modalInput}
                >
                  <option value="low">🟢 Низкий</option>
                  <option value="medium">🟡 Средний</option>
                  <option value="high">🔴 Высокий</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, color: '#888', marginBottom: 2, display: 'block' }}>Срок выполнения</label>
                <input 
                  type="date" 
                  value={taskForm.dueDate} 
                  onChange={e => setTaskForm(p => ({...p, dueDate: e.target.value}))} 
                  style={modalInput}
                />
              </div>
            </div>
            
            <div style={{display:'grid', gridTemplateColumns:'1fr 2fr', gap:10, marginTop:10}}>
              <select value={selectedLinkedType} onChange={e => setSelectedLinkedType(e.target.value)} style={modalInput}>
                <option value="">Привязать к...</option>
                <option value="client">Клиенту</option>
                <option value="employee">Сотруднику</option>
                <option value="candidate">Кандидату</option>
                <option value="vacancy">Вакансии</option>
              </select>
              {selectedLinkedType && (
                <select value={selectedLinkedId} onChange={e => setSelectedLinkedId(e.target.value)} style={modalInput}>
                  <option value="">Выберите...</option>
                  {selectedLinkedType === 'client' && clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  {selectedLinkedType === 'employee' && employees.map(e => <option key={e.id} value={e.id}>{e.lastName} {e.firstName}</option>)}
                  {selectedLinkedType === 'candidate' && candidates.map(c => <option key={c.id} value={c.id}>{c.lastName} {c.firstName}</option>)}
                  {selectedLinkedType === 'vacancy' && vacancies.map(v => <option key={v.id} value={v.id}>{v.title}</option>)}
                </select>
              )}
            </div>
            
            <div style={modalFooter}>
              {editingTask && (
                <button onClick={() => { deleteTask(editingTask._id); setShowTaskModal(false); }} style={{...modalBtnCancel, color:'#ff4d4f', borderColor:'#ff4d4f'}}>
                  <Trash2 size={14}/> Удалить
                </button>
              )}
              <button onClick={saveTask} style={modalBtnPrimary}><Save size={14}/> Сохранить</button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
};

// ================= СТИЛИ =================
const styles: Record<string, React.CSSProperties> = {
  root: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1100,
    display: 'flex', flexDirection: 'column',
    background: 'linear-gradient(135deg, #f0f4f8 0%, #e8ecf1 100%)',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 24px', background: 'rgba(255,255,255,0.7)',
    borderBottom: '1px solid rgba(0,0,0,0.06)', flexShrink: 0,
  },
  title: {
    fontSize: 20, fontWeight: 700, margin: 0, color: '#1a1a2e',
    background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  closeBtn: {
    background: 'rgba(255,255,255,0.8)', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 10,
    width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', color: '#666', backdropFilter: 'blur(8px)',
    transition: 'all 0.2s',
  },
  toolbar: {
    display: 'flex', gap: 10, alignItems: 'center',
    padding: '10px 24px', background: 'rgba(255,255,255,0.5)',
    borderBottom: '1px solid rgba(0,0,0,0.04)', flexShrink: 0, flexWrap: 'wrap',
  },
  modeBtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '6px 14px', borderRadius: 20, border: '1px solid #2575fc',
    cursor: 'pointer', fontSize: 13, fontWeight: 500,
    transition: 'all 0.2s',
  },
  observerBox: {
    display: 'flex', alignItems: 'center', gap: 6,
    background: 'rgba(255,255,255,0.7)', borderRadius: 20, padding: '4px 12px',
    border: '1px solid rgba(0,0,0,0.06)',
  },
  select: {
    border: 'none', background: 'transparent', outline: 'none',
    fontSize: 12, color: '#333', cursor: 'pointer',
  },
  searchBox: {
    display: 'flex', alignItems: 'center', flex: 1, maxWidth: 240,
    background: 'rgba(255,255,255,0.7)', borderRadius: 20,
    padding: '5px 12px', border: '1px solid rgba(0,0,0,0.06)',
  },
  searchInput: { border: 'none', outline: 'none', background: 'transparent', flex: 1, fontSize: 12 },
  btnPrimary: {
    background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: 'white',
    border: 'none', borderRadius: 20, padding: '6px 14px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500,
    boxShadow: '0 4px 10px rgba(37,117,252,0.3)', transition: 'all 0.2s',
  },
  btnSecondary: {
    background: 'rgba(255,255,255,0.7)', border: '1px solid #2575fc', color: '#2575fc',
    borderRadius: 20, padding: '6px 14px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500,
    transition: 'all 0.2s',
  },
  hint: {
    padding: '6px 24px', background: 'rgba(37,117,252,0.06)', borderBottom: '1px solid rgba(37,117,252,0.12)',
    fontSize: 12, color: '#2575fc',
  },
  loading: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize: 14 },
  board: {
    flex: 1, display: 'flex', gap: 14, padding: '14px 24px 24px',
    overflowX: 'auto', overflowY: 'hidden', alignItems: 'flex-start',
    scrollBehavior: 'smooth',
  },
  column: {
    width: 290, flexShrink: 0, borderRadius: 20, padding: 14,
    display: 'flex', flexDirection: 'column',
    maxHeight: '100%', minHeight: 420,
    border: '1px solid rgba(255,255,255,0.3)',
    transition: 'background 0.25s, border-color 0.25s, box-shadow 0.25s',
  },
  colHead: {
    display: 'flex', alignItems: 'center', gap: 8,
    marginBottom: 12, flexShrink: 0,
  },
  colDot: { width: 10, height: 10, borderRadius: 5, flexShrink: 0 },
  colTitle: { fontSize: 14, fontWeight: 700, color: '#1a1a2e', flex: 1 },
  colCount: {
    color: 'white', fontSize: 11, fontWeight: 700,
    minWidth: 22, height: 22, borderRadius: 11,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
  },
  colActions: { display: 'flex', gap: 2 },
  colActBtn: {
    background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(0,0,0,0.06)',
    borderRadius: 6, padding: 4, cursor: 'pointer', display: 'flex',
    backdropFilter: 'blur(4px)', transition: 'all 0.15s',
  },
  colBody: {
    flex: 1, overflowY: 'auto', minHeight: 0,
    display: 'flex', flexDirection: 'column', gap: 10, paddingRight: 2,
  },
  card: {
    borderRadius: 12, padding: 12,
    border: '1px solid rgba(0,0,0,0.04)',
    transition: 'all 0.2s cubic-bezier(0.2, 0.0, 0, 1.0)',
    cursor: 'grab',
  },
  cardTop: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 8,
  },
  cardTitle: { fontSize: 13, fontWeight: 600, flex: 1, color: '#1a1a2e' },
  cardMeta: {
    fontSize: 11, display: 'flex', gap: 10, marginBottom: 6, color: '#555',
  },
  cardLink: {
    fontSize: 10, display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4,
  },
  cardFiles: { fontSize: 10 },
};

// Стили модальных окон (общие)
const modalOverlay: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 9999999,
};
const modalBox: React.CSSProperties = {
  background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(20px)',
  borderRadius: 24, padding: 28, width: 440, maxWidth: '92%',
  maxHeight: '85vh', overflowY: 'auto',
  boxShadow: '0 30px 60px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.3) inset',
  border: '1px solid rgba(255,255,255,0.4)',
};
const modalHead: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20,
};
const modalClose: React.CSSProperties = {
  background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: 10, width: 34, height: 34, display: 'flex',
  alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  backdropFilter: 'blur(6px)', transition: 'all 0.2s',
};
const modalInput: React.CSSProperties = {
  width: '100%', padding: '10px 14px', borderRadius: 12,
  border: '1px solid rgba(0,0,0,0.08)', background: 'rgba(255,255,255,0.8)',
  fontSize: 13, outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.2s, box-shadow 0.2s',
};
const modalFooter: React.CSSProperties = {
  display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24,
  paddingTop: 16, borderTop: '1px solid rgba(0,0,0,0.05)',
};
const modalBtnPrimary: React.CSSProperties = {
  background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: 'white',
  border: 'none', borderRadius: 30, padding: '9px 22px', cursor: 'pointer',
  display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600,
  boxShadow: '0 6px 14px rgba(37,117,252,0.3)', transition: 'all 0.2s',
};
const modalBtnCancel: React.CSSProperties = {
  background: 'transparent', border: '1px solid rgba(0,0,0,0.1)', color: '#666',
  borderRadius: 30, padding: '9px 22px', cursor: 'pointer',
  fontSize: 13, fontWeight: 500, transition: 'all 0.2s',
};
const errBlock: React.CSSProperties = {
  background: 'rgba(255,77,79,0.08)', border: '1px solid rgba(255,77,79,0.2)',
  borderRadius: 10, padding: '8px 14px', color: '#ff4d4f',
  fontSize: 13, marginBottom: 16, textAlign: 'center',
};

export default TaskKanbanModal;